import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { useSubscriptionStore } from '@/stores/subscription'
import { TierBadge } from '@/components/TierBadge'
import { CHARGEE_SITE_URL, EASYENERGY_SITE_URL } from '@/lib/branding'

/** The official easyEnergy wordmark. Use `variant="white"` on orange surfaces. */
export function EasyEnergyLogo({
  className,
  variant = 'white',
}: {
  className?: string
  variant?: 'white' | 'color'
}) {
  return (
    <img
      src={variant === 'white' ? '/easyenergy/logo-white.svg' : '/easyenergy/logo.svg'}
      alt="easyEnergy"
      className={className ?? 'h-[18px] w-auto'}
    />
  )
}

/** Small "powered by Chargee" mark shown next to the easyEnergy branding. */
export function PoweredByChargee({ light = false }: { light?: boolean }) {
  return (
    <a
      href={CHARGEE_SITE_URL}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center gap-1.5 text-11 font-semibold uppercase tracking-wide transition-opacity hover:opacity-80 ${
        light ? 'text-white/80' : 'text-text-gray'
      }`}
    >
      powered by
      <img
        src={light ? '/chargee-logo-text-white.svg' : '/chargee-logo-text.svg'}
        alt="Chargee"
        className="h-3 w-auto"
      />
    </a>
  )
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => Boolean(s.sessionToken))
  const user = useAuthStore((s) => s.userDetails?.user ?? null)
  const logout = useAuthStore((s) => s.logout)
  const clearSubscription = useSubscriptionStore((s) => s.clear)
  const subscription = useSubscriptionStore((s) => s.subscription)

  function handleLogout() {
    logout()
    clearSubscription()
    navigate('/login')
  }

  return (
    <div className="flex min-h-screen flex-col">
      {/* easyEnergy header: fixed-height orange bar with the white wordmark */}
      <header className="sticky top-0 z-20 bg-orange shadow-btn-shadow">
        <div className="mx-auto flex h-60 w-full max-w-6xl items-center justify-between gap-6 px-4">
          <div className="flex items-center gap-4">
            <Link to={isAuthenticated ? '/services' : '/login'} aria-label="easyEnergy diensten">
              <EasyEnergyLogo className="h-5 w-auto md:h-6" />
            </Link>
            <span className="hidden sm:block">
              <PoweredByChargee light />
            </span>
          </div>
          <div className="flex items-center gap-4">
            {isAuthenticated && subscription && (
              <TierBadge
                tier={subscription.tier}
                className="!h-6 !px-2.5 !text-11"
                title={
                  subscription.status === 'included'
                    ? 'Inbegrepen bij easyEnergy — easyEnergy betaalt je abonnement'
                    : 'Je abonnement (via Chargee)'
                }
              />
            )}
            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                {user && (
                  <span className="hidden text-15 text-white/80 sm:inline">
                    {user.firstName} {user.lastName}
                  </span>
                )}
                <button
                  className="inline-flex h-10 items-center justify-center rounded-20 border border-white/50 px-4 text-15 font-bold text-white transition-all duration-300 hover:bg-white hover:text-orange"
                  onClick={handleLogout}
                >
                  Uitloggen
                </button>
              </div>
            ) : (
              <a
                href={EASYENERGY_SITE_URL}
                className="text-15 font-semibold text-white/90 transition-all duration-300 hover:text-white"
              >
                easyenergy.com
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <SiteFooter />
    </div>
  )
}

const FOOTER_LINK = 'transition-all duration-300 text-white hover:text-white/70'

const EASYENERGY_LINKS = [
  { label: 'over easyEnergy', href: `${EASYENERGY_SITE_URL}/over-ons` },
  { label: 'contact', href: `${EASYENERGY_SITE_URL}/contact` },
  { label: 'voorwaarden', href: `${EASYENERGY_SITE_URL}/voorwaarden` },
  { label: 'privacy', href: `${EASYENERGY_SITE_URL}/privacy-statement-easyenergy` },
  { label: 'cookies', href: `${EASYENERGY_SITE_URL}/cookie-statement` },
  { label: 'stroometiket', href: `${EASYENERGY_SITE_URL}/stroometiket` },
  { label: 'werken bij', href: `${EASYENERGY_SITE_URL}/werken-bij` },
]

const SOCIAL_LINKS = [
  {
    label: 'instagram',
    href: 'https://www.instagram.com/easyenergy_com/',
    path: 'M8 0C5.829 0 5.556.01 4.703.048C3.85.088 3.269.222 2.76.42a3.9 3.9 0 0 0-1.417.923A3.9 3.9 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7C.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297c.04.852.174 1.433.372 1.942c.205.526.478.972.923 1.417c.444.445.89.719 1.416.923c.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.9 3.9 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417c.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.9 3.9 0 0 0-.923-1.417A3.9 3.9 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046c.78.035 1.204.166 1.486.275c.373.145.64.319.92.599s.453.546.598.92c.11.281.24.705.275 1.485c.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.5 2.5 0 0 1-.599.919c-.28.28-.546.453-.92.598c-.28.11-.704.24-1.485.276c-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.5 2.5 0 0 1-.92-.598a2.5 2.5 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485c-.038-.843-.046-1.096-.046-3.233s.008-2.388.046-3.231c.036-.78.166-1.204.276-1.486c.145-.373.319-.64.599-.92s.546-.453.92-.598c.282-.11.705-.24 1.485-.276c.738-.034 1.024-.044 2.515-.045zm4.988 1.328a.96.96 0 1 0 0 1.92a.96.96 0 0 0 0-1.92m-4.27 1.122a4.109 4.109 0 1 0 0 8.217a4.109 4.109 0 0 0 0-8.217m0 1.441a2.667 2.667 0 1 1 0 5.334a2.667 2.667 0 0 1 0-5.334',
  },
  {
    label: 'linkedin',
    href: 'https://www.linkedin.com/company/easyenergy.com/',
    path: 'M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854zm4.943 12.248V6.169H2.542v7.225zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248c-.015-.709-.52-1.248-1.342-1.248S2.4 3.226 2.4 3.934c0 .694.521 1.248 1.327 1.248zm4.908 8.212V9.359c0-.216.016-.432.08-.586c.173-.431.568-.878 1.232-.878c.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252c-1.274 0-1.845.7-2.165 1.193v.025h-.016l.016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225z',
  },
  {
    label: 'facebook',
    href: 'https://www.facebook.com/profile.php?id=61566342541217',
    path: 'M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131c.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951',
  },
]

/** easyEnergy footer: orange surface, white links, powered-by-Chargee credit. */
function SiteFooter() {
  return (
    <footer className="mt-24 bg-orange pb-10 pt-12 text-white shadow-[inset_0_4px_4px_rgba(0,0,0,0.25)] md:mt-32">
      <div className="mx-auto w-full max-w-6xl px-4">
        <div className="flex flex-col justify-between gap-10 md:flex-row">
          <div className="max-w-sm">
            <a href={EASYENERGY_SITE_URL} target="_blank" rel="noreferrer">
              <img src="/easyenergy/easyenergy-white.svg" alt="easyEnergy" className="h-6 w-auto" />
            </a>
            <p className="mt-4 text-base leading-normal text-white/90">
              gewoon. goed. geregeld. Energie op basis van de échte beursprijs.
            </p>
            <div className="mt-5">
              <PoweredByChargee light />
              <p className="mt-1.5 max-w-xs text-13 leading-normal text-white/70">
                Deze dienst draait op het Chargee-platform: je logt in met je Chargee-account en
                je Sparky levert de meetdata.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-10 sm:flex-row sm:gap-16">
            <div>
              <h4 className="text-base font-semibold text-white">easyEnergy</h4>
              <ul className="mt-4 flex flex-col gap-2 text-15">
                {EASYENERGY_LINKS.map((link) => (
                  <li key={link.label}>
                    <a href={link.href} target="_blank" rel="noreferrer" className={FOOTER_LINK}>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold text-white">Volg ons</h4>
              <ul className="mt-4 flex flex-col gap-2 text-15">
                {SOCIAL_LINKS.map((social) => (
                  <li key={social.label}>
                    <a
                      href={social.href}
                      target="_blank"
                      rel="noreferrer"
                      className={`flex items-center gap-3 ${FOOTER_LINK}`}
                    >
                      <span className="size-4 text-white/80">
                        <svg xmlns="http://www.w3.org/2000/svg" className="size-4" viewBox="0 0 16 16">
                          <path fill="currentColor" d={social.path} />
                        </svg>
                      </span>
                      {social.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-10 border-t border-white/25 pt-5 text-13 text-white/70">
          © {new Date().getFullYear()} easyEnergy · powered by Chargee
        </div>
      </div>
    </footer>
  )
}
